/**
 * Money is integer cents everywhere — in the database, in the app, on the
 * forms. Floats produce off-by-a-penny tax that gets printed on signed
 * documents. There is no float path.
 */

export type Cents = number;

/**
 * Parse user input to cents. Returns null when it is not a usable amount.
 *
 * Blank is null, not zero. It used to be zero, and the difference matters:
 * a caller asking "did they type an amount?" got back a perfectly valid $0.00
 * and saved it. That is how a blank box became a zero-dollar expense line.
 * Callers for whom blank genuinely means nothing already write `?? 0`.
 */
export function parseMoney(input: string): Cents | null {
  const s = input.trim().replace(/[$,\s]/g, "");
  if (s === "") return null;
  if (s.startsWith("-")) return null;          // negatives are never valid here
  if (!/^\d*\.?\d{0,2}$/.test(s)) return null;
  const n = Number.parseFloat(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const USD0 = new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD",
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

/** $2,700.00 — itemizations and printed forms. */
export const formatMoney = (c: Cents): string => USD.format(c / 100);

/** $2,700 — list views, where cents on whole-dollar cars is noise. */
export const formatMoneyShort = (c: Cents): string => USD0.format(c / 100);

export const centsToInput = (c: Cents): string => (c / 100).toFixed(2);
